// src/belediye/components/mesajlasma/MahallePicker.js
// FAZ-55 — Detaylı görevde MAHALLE seçimi.
//
// FAZ-55/8 (kullanıcı kararı 2026-07-28) — ÇOKLU SEÇİM + LİSTE:
//   • "mahalleler çipler şeklinde gelmesin, liste şeklinde kaydırılabilir ve
//     aranabilir şekilde gelsin" → seçenekler artık ÇİP DEĞİL, kaydırılabilir
//     bir liste. (FAZ-55/6'da çip yapmıştık; 100+ mahallede çip duvarı
//     okunmuyor, liste tarama için doğru biçim.)
//   • "ama her seçilen mahalle çip olarak mesaj boşluğuna gelsin" → SEÇİLENLER
//     çip olur ve mesaj kutusunun İÇİNDE durur (WhatsApp'ta alıcı çipleri gibi).
//   • "en az bir mahalle seçilince gönder butonu aktif olsun, bunun yanında da
//     atla butonu olsun her zaman" → Gönder ≥1 seçimde açılır; Atla her zaman
//     görünür ve HEP AYNI YERDE (kutunun içinde, gönderin solunda).
//
// Neden çoklu: bir iş birden çok mahalleyi kapsayabilir (ana cadde temizliği,
// kar küreme güzergâhı). Tek FK bunu ancak "görevi böl" diyerek anlatabiliyordu.
//
// FAZ-83/Y2: artık YerAdimi'nin bir SEKMESİ olarak da çiziliyor. O hâlde
// (`gomulu`) kendi Atla/Gönder satırını TAŞIMAZ — tek alt satır YerAdimi'nindir;
// seçim her değiştiğinde `onDegis` ile yukarı bildirilir. Tek başına kullanım
// (eski çağıranlar) hiç değişmedi.
//
// Sözleşme: onDone({ neighborhood_ids: [id…], neighborhood_names: [ad…] })
//           onSkip() — mahalle sorusunu tümüyle atlar.
//           gomulu + onDegis([{id,name}…]) — sekme kipi.
import React, { useEffect, useMemo, useState } from "react";
import { CheckIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { fetchNeighborhoods } from "../../services/belediyeApi";
import SihirbazAltSatir from "./SihirbazAltSatir";
import useMobilKlavye from "./useMobilKlavye";

// FAZ-83: sekmeler arası gidip gelmek listeyi HER SEFERİNDE yeniden
// indirtmesin. Mahalle listesi bir oturum boyunca değişmeyen bir veri;
// modül kapsamındaki bu önbellek onu bir kez indirir.
let mahalleOnbellek = null;

export default function MahallePicker({
  initial = [],
  onDone,
  onSkip,
  gomulu = false,
  onDegis,
  // Sekme kipinde alt satırın düğmeleri ADIMIN tamamına aittir; YerAdimi
  // kendi Atla/Gönder ayarlarını buradan geçirir.
  altSatir = null,
}) {
  const [mahalleler, setMahalleler] = useState(mahalleOnbellek); // null = yükleniyor
  const [arama, setArama] = useState("");
  const [secili, setSecili] = useState(initial); // [{id,name}] — SIRA korunur
  // FAZ-83/5 — MOBİLDE ARAMA KUTUSU KENDİLİĞİNDEN AÇILMAZ.
  // Kullanıcı (2026-08-01): "mahallede hem arama yapılıyor — mesajdan klavyeye
  // lazım — hem de seçim; bu ekranı çok kapatıyor. Belki mobilde mesajdan
  // aramayı kapatabiliriz, mahalleler direkt seçilir."
  // Dokunmatikte varsayılan kip SEÇİM: klavye hiç açılmaz, liste tam boyunda
  // görünür. Arayan kullanıcı "Ara" düğmesine basar; kutu o zaman açılır ve
  // odaklanır. Masaüstünde davranış değişmedi (kutu açık ve odaklı gelir).
  const mobil = useMobilKlavye();
  const [aramaAcik, setAramaAcik] = useState(!mobil);

  useEffect(() => {
    if (mahalleOnbellek) return; // önbellekten geldi, istek yok
    let iptal = false;
    fetchNeighborhoods()
      .then((d) => {
        const liste = Array.isArray(d) ? d : d?.results || [];
        mahalleOnbellek = liste;
        if (!iptal) setMahalleler(liste);
      })
      .catch(() => !iptal && setMahalleler([]));
    return () => {
      iptal = true;
    };
  }, []);

  const suzulmus = useMemo(() => {
    const liste = mahalleler || [];
    const q = arama.trim().toLocaleLowerCase("tr");
    if (!q) return liste;
    return liste.filter((m) => (m.name || "").toLocaleLowerCase("tr").includes(q));
  }, [mahalleler, arama]);

  const seciliMi = (m) => secili.some((s) => Number(s.id) === Number(m.id));
  // FAZ-83: yeni seçim ÖNCE hesaplanır, sonra hem yerel duruma yazılır hem de
  // (gömülü kipte) yukarı bildirilir. `setSecili` içinde onDegis çağırmak
  // render sırasında ebeveyni güncellemek olurdu.
  const degistir = (m) => {
    const yeni = secili.some((s) => Number(s.id) === Number(m.id))
      ? secili.filter((s) => Number(s.id) !== Number(m.id))
      : [...secili, { id: m.id, name: m.name }];
    setSecili(yeni);
    onDegis && onDegis(yeni);
  };

  const gonder = () => {
    if (!secili.length) return;
    onDone({
      neighborhood_ids: secili.map((s) => s.id),
      neighborhood_names: secili.map((s) => s.name),
    });
  };

  // Çipler + arama kutusu — iki kipte de AYNI içerik; değişen yalnız onu
  // saran kabuk (gömülüde çıplak kutu, tek başınayken ortak alt satır).
  const icerik = (
    <div className="msj-mahalle-icerik">
      {secili.map((s) => (
        <span key={s.id} className="msj-mahalle-cip">
          {s.name}
          <button
            type="button"
            onClick={() => degistir(s)}
            title={`${s.name} seçimini kaldır`}
            aria-label={`${s.name} seçimini kaldır`}
          >
            <XMarkIcon />
          </button>
        </span>
      ))}
      {/* Mobilde kapalıyken kutunun içinde ne yapılacağını söyleyen kısa bir
          not + "Ara" düğmesi durur; klavye ancak Ara'ya basılınca açılır. */}
      {!aramaAcik && (
        <>
          {!secili.length && <span className="msj-mahalle-not">Listeden seçin</span>}
          <button
            type="button"
            className="msj-mahalle-ara-btn"
            onClick={() => setAramaAcik(true)}
            title="Mahalle ara"
          >
            <MagnifyingGlassIcon /> Ara
          </button>
        </>
      )}
      {aramaAcik && (
      <textarea
        rows={1}
        value={arama}
        placeholder={secili.length ? "Başka mahalle ara…" : "Mahalle ara…"}
        aria-label="Mahalle ara"
        autoFocus
        onChange={(e) => setArama(e.target.value)}
        onKeyDown={(e) => {
          // Boş aramada Backspace son çipi siler (mesaj kutusu alışkanlığı).
          if (e.key === "Backspace" && !arama && secili.length) {
            e.preventDefault();
            const yeni = secili.slice(0, -1);
            setSecili(yeni);
            onDegis && onDegis(yeni);
            return;
          }
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            // Arama tek mahalleye indiyse Enter onu seçer ve aramayı boşaltır
            // (peş peşe yazarak seçme). Aksi hâlde seçim varsa gönderir.
            if (suzulmus.length === 1) {
              degistir(suzulmus[0]);
              setArama("");
            } else if (!gomulu) {
              // Gömülü kipte adımı ilerletmek YerAdimi'nin işi — burada Enter
              // sessizce hiçbir şey yapmaz, yanlışlıkla adım atlanmaz.
              gonder();
            }
          }
        }}
      />
      )}
    </div>
  );

  return (
    <div className="msj-mahalle">
      {/* KAYDIRILABİLİR LİSTE — seçilenler işaretli kalır, liste kapanmaz;
          arka arkaya birkaç mahalle seçmek tek ekranda biter. */}
      <div className="msj-mahalle-liste" role="listbox" aria-multiselectable="true">
        {mahalleler === null && <span className="msj-mahalle-bos">Mahalleler yükleniyor…</span>}
        {mahalleler !== null && suzulmus.length === 0 && (
          <span className="msj-mahalle-bos">Eşleşen mahalle yok.</span>
        )}
        {suzulmus.map((m) => {
          const isar = seciliMi(m);
          return (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={isar}
              className={`msj-mahalle-sat ${isar ? "is-secili" : ""}`}
              onClick={() => degistir(m)}
            >
              <span className="msj-mahalle-ad">{m.name}</span>
              {isar && <CheckIcon className="msj-mahalle-tik" />}
            </button>
          );
        })}
      </div>

      {/* FAZ-83/2 — ARAMA VE SEÇİLENLER ANA MESAJ KUTUSUNDA.
          Kullanıcı: "mahalle arama kısmı ana mesaj kutusunda olsun ve seçilen
          mesajlar yine buraya yazılsın." Yani bu adımın da AYRI bir kutusu
          yok; çipler ve arama, sihirbazın tek alt satırının içinde durur.
          Sekme kipinde Atla/Gönder YerAdimi'nin (adımın tamamının) işidir —
          `altSatir` ile o gelir; tek başına kullanımda kendi işidir. */}
      <SihirbazAltSatir
        sinif="msj-mahalle-kutu"
        {...(altSatir || {
          onSkip,
          skipTitle: "Mahalle seçmeden devam et",
          gonderAktif: !!secili.length,
          onGonder: gonder,
          gonderTitle: "Seçilenlerle devam et",
        })}
      >
        {icerik}
      </SihirbazAltSatir>
    </div>
  );
}
